import React from 'react'
import { useTranslation } from 'react-i18next'
import { Dialog, DialogClose, DialogContent, DialogTitle } from '@/components/ui/dialog'
import { Avatar } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { ArrowRight, Coins, Maximize2, MessageCircle, Pencil, Trophy, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useAuth } from '@/lib/AuthContext'
import { useRoute } from '@/lib/RouteContext'
import { BADGE_MANAGERS, TEAM_ROLES } from '@/lib/permissions'
import { memberPagePath } from '@/lib/memberPage'
import { ROUTES } from '@/lib/routes'
import { useOptionalChat } from '@/components/features/chat/ChatProvider'
import { ConnectionActions } from '@/components/features/chat/ChatConnections'
import type { ChatConnectionState } from '@/lib/sdk-official/resources/chat'
import { BadgeRow } from '@/components/features/badges/BadgeChip'
import { AdjustPointsDialog } from '@/components/features/badges/AdjustPointsDialog'
import { ItemUnavailable } from '@/components/shared/ItemUnavailable'
import { LevelProgressCard } from '@/components/features/ranking/LevelProgressCard'
import { levelNameOf } from '@/components/features/ranking/rankingModel'
import { Headline, MemberCover, NameWithFlag, RoleChip, shownNameOf, useMemberPage } from './memberPageParts'
import type { MemberPageData } from '@/types/memberPage'

interface UserProfileDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  memberId: string
}

/** The round, dark, translucent buttons over the cover: legible on any image or gradient. */
const overCoverButton = 'flex h-10 w-10 items-center justify-center rounded-full bg-black/45 text-white backdrop-blur-sm transition-colors hover:bg-black/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-black/40'
const plainCornerButton = 'flex h-10 w-10 items-center justify-center rounded-full text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary'

/**
 * F-177 criterion 1a: the short preview a name or avatar opens. It never
 * repeats the page: no tabs, no About, links, Formação, Experience or
 * "Na comunidade". The expand icon and "Ver perfil completo" lead to
 * `/_/members/{id}` and close the dialog, and only appear when that page
 * exists for the viewer (signed out: when the public page answers).
 */
export const UserProfileDialog = ({ open, onOpenChange, memberId }: UserProfileDialogProps) => {
  const { t, i18n } = useTranslation(['members', 'common', 'chat', 'badges', 'ranking'])
  const { user, hasRole, isAuthenticated } = useAuth()
  const { navigate } = useRoute()
  const signedOut = !isAuthenticated
  const { member, setMember, loading, error, reload } = useMemberPage(memberId, { signedOut, enabled: open })
  const chat = useOptionalChat()
  const [messageError, setMessageError] = React.useState<string | null>(null)
  const [openingChat, setOpeningChat] = React.useState(false)
  const [adjustOpen, setAdjustOpen] = React.useState(false)

  const isOwnProfile = Boolean(user?.id) && user?.id === memberId
  const isTeam = !signedOut && hasRole(TEAM_ROLES)
  const shownName = shownNameOf(member)
  const limited = Boolean(member?.limited) && !isOwnProfile
  const connection: ChatConnectionState = member?.connection || 'none'
  const connectionMode = chat?.me?.startMode === 'CONNECTION' || limited
  const needsConnection = Boolean(!signedOut && chat?.enabled && !isOwnProfile && connectionMode && connection !== 'connected')
  const canMessage = Boolean(!signedOut && chat && !isOwnProfile && chat.canMessage(memberId, { chatAvailable: member?.chatAvailable }))
  const isOnline = Boolean(!signedOut && chat?.isOnline(memberId))

  const engagement = member?.engagement ?? null
  const showScore = Boolean(engagement?.gamificationEnabled && engagement.points != null)
  const canAdjust = !signedOut && showScore && hasRole(BADGE_MANAGERS)
  const num = (n: number) => new Intl.NumberFormat(i18n.language).format(n)
  const since = member?.joinedAt || member?.communityStats?.memberSince || member?.memberSince
  const joinedOn = since ? new Intl.DateTimeFormat(i18n.language, { month: 'short', year: 'numeric' }).format(new Date(since)) : ''

  /* The page exists for whoever got the preview: signed in, any answer (the limited one too); signed out, the public one. */
  const pageExists = Boolean(member)
  const openPage = () => {
    onOpenChange(false)
    navigate(memberPagePath(memberId))
  }

  const startChat = async () => {
    if (!chat || !member) return
    setOpeningChat(true)
    setMessageError(null)
    try {
      await chat.openWith(memberId, { id: memberId, name: shownName, avatar: member.avatarUrl, jobTitle: member.jobTitle, tagline: member.tagline }, connection)
      if (window.innerWidth >= 1024 && chat.panelState === 'COLLAPSED') chat.setPanelState('EXPANDED')
      onOpenChange(false)
    } catch (err) {
      const e = err as { response?: { data?: { message?: string } } }
      setMessageError(e.response?.data?.message || t('chat:errors.cannotMessage'))
    } finally {
      setOpeningChat(false)
    }
  }

  /* Over a cover photo the corner buttons need their dark veil; on the plain white header they are quiet icons. */
  const hasCover = Boolean(member?.coverUrl)
  const cornerButton = hasCover ? overCoverButton : plainCornerButton
  const corner = (
    <div className="absolute right-3 top-3 z-10 flex items-center gap-2" data-profile-preview-actions>
      {pageExists && (
        <button type="button" onClick={openPage} className={cornerButton} aria-label={t('page.expand')} title={t('page.expand')} data-profile-expand>
          <Maximize2 className="h-4 w-4" aria-hidden />
        </button>
      )}
      <DialogClose className={cornerButton} aria-label={t('common:close')} title={t('common:close')} data-profile-close>
        <X className="h-4 w-4" aria-hidden />
      </DialogClose>
    </div>
  )

  if (loading || (!member && !error)) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent hideClose className="w-[calc(100%-1.5rem)] max-w-lg overflow-hidden rounded-2xl p-0 sm:p-0">
          <DialogTitle className="sr-only">{t('profileDialog.title')}</DialogTitle>
          <div className="relative">
            <div className="h-1 bg-gray-200" />
            {corner}
          </div>
          <div className="animate-pulse space-y-3 px-6 pb-8 pt-5">
            <div className="flex items-center gap-4 pr-24">
              <div className="h-16 w-16 flex-shrink-0 rounded-full bg-gray-200" />
              <div className="flex-1 space-y-2"><div className="h-6 w-2/3 rounded bg-gray-200" /><div className="h-4 w-1/2 rounded bg-gray-200" /></div>
            </div>
            <div className="grid grid-cols-3 gap-3 pt-2">{[1, 2, 3].map((i) => <div key={i} className="h-16 rounded-xl bg-gray-200" />)}</div>
          </div>
        </DialogContent>
      </Dialog>
    )
  }

  if (error || !member) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="w-[calc(100%-1.5rem)] max-w-lg p-4 sm:p-8">
          <DialogTitle className="sr-only">{t('profileDialog.title')}</DialogTitle>
          <ItemUnavailable kind={error || 'missing'} item="member" onBack={() => onOpenChange(false)} backLabel={t('common:close')} onRetry={() => void reload()} padded={false} className="py-6 [&>div]:shadow-none" />
        </DialogContent>
      </Dialog>
    )
  }

  const groups = (member as MemberPageData & { groups?: { id: string; name: string }[] }).groups
  const tags = member.tags
  const showGroupsTags = !signedOut && (isOwnProfile || isTeam)

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* A grid: every wrapper carries `min-w-0`, or a long word sets the width of the whole dialog. */}
      <DialogContent hideClose className="w-[calc(100%-1.5rem)] max-w-lg gap-0 overflow-hidden rounded-2xl border border-gray-200 bg-white p-0 sm:p-0 shadow-[0_8px_32px_rgba(0,0,0,0.04)]" data-profile-preview>
        <DialogTitle className="sr-only">{t('profileDialog.title')}</DialogTitle>
        {/* Most people have no cover: then a compact header with a thin brand line, never an empty colored block. */}
        <div className="relative min-w-0">
          {hasCover
            ? <MemberCover url={member.coverUrl} className="h-20" />
            : <div className="h-1 w-full bg-gradient-to-r from-brand-500 to-brand-600" aria-hidden data-member-cover-line />}
          {corner}
        </div>

        <div className="min-w-0 px-5 pb-6 pt-4 sm:px-6">
          <div className={cn('flex min-w-0 items-start gap-4', !hasCover && 'pr-24')}>
            <div className={cn('relative flex-shrink-0', hasCover && '-mt-10')}>
              <Avatar src={member.avatarUrl} alt={shownName} fallback={shownName} className={cn('h-16 w-16', hasCover && 'h-[72px] w-[72px] border-4 border-white shadow-sm')} />
              {isOnline && <div className="absolute bottom-0 right-0 h-4 w-4 rounded-full border-[3px] border-white bg-green-500" data-profile-online />}
            </div>
            <div className={cn('min-w-0 flex-1', !hasCover && 'pt-1')}>
              <div className="flex min-w-0 flex-wrap items-center gap-2">
                <NameWithFlag name={shownName} country={member.country} className="text-xl" />
                <RoleChip role={member.role} />
              </div>
              <Headline jobTitle={member.jobTitle} company={member.company} className="mt-1 text-sm" />
            </div>
          </div>
          {member.tagline && (
            <p className="mt-2 line-clamp-3 whitespace-pre-line break-words text-sm text-gray-600 [overflow-wrap:anywhere]" data-profile-tagline>
              {member.tagline}
            </p>
          )}
          {engagement && engagement.badges?.length > 0 && (
            <BadgeRow className="mt-3 gap-1.5" size="card" max={5} badges={engagement.badges.map((grant) => ({ badge: grant.badge, grant }))}
              onMore={pageExists ? () => { onOpenChange(false); navigate(memberPagePath(memberId, 'achievements')) } : undefined} />
          )}
          {showGroupsTags && ((groups?.length ?? 0) > 0 || (tags?.length ?? 0) > 0) && (
            <div className="mt-3 flex flex-wrap gap-1.5" data-profile-groups-tags>
              {groups?.map((g) => <span key={g.id} className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-700">{g.name}</span>)}
              {tags?.map((tag) => <span key={tag} className="rounded-full border border-gray-200 px-2 py-0.5 text-xs text-gray-600">{tag}</span>)}
            </div>
          )}

          {limited ? (
            <p className="mt-4 rounded-lg bg-gray-50 p-3 text-sm text-gray-600" data-profile-limited>{t('page.connectToSee')}</p>
          ) : (
            <div className={cn('mt-5 grid gap-3', showScore ? 'grid-cols-3' : 'grid-cols-1')} data-profile-stats>
              {joinedOn && (
                <div className="flex flex-col items-center justify-center rounded-xl bg-gray-50 p-3 text-center">
                  <span className="text-base font-bold text-gray-900">{joinedOn}</span>
                  <span className="text-xs text-gray-500">{t('page.memberSinceShort')}</span>
                </div>
              )}
              {showScore && engagement && (
                <>
                  <div className="flex flex-col items-center justify-center rounded-xl bg-gray-50 p-3" data-profile-points>
                    <span className="text-base font-bold tabular-nums text-gray-900">{num(engagement.points ?? 0)}</span>
                    <span className="text-xs text-gray-500">{t('badges:profile.points')}</span>
                  </div>
                  <div className="flex flex-col items-center justify-center rounded-xl bg-gray-50 p-3" data-profile-level>
                    <span className="flex max-w-full items-center gap-1 text-base font-bold text-gray-900">
                      {engagement.levelImageUrl ? <img src={engagement.levelImageUrl} alt="" className="h-5 w-5 flex-shrink-0 rounded object-cover" /> : <Trophy className="h-4 w-4 flex-shrink-0 text-primary" aria-hidden />}
                      <span className="truncate" title={levelNameOf(t, engagement.level, engagement.levelName)}>{levelNameOf(t, engagement.level, engagement.levelName)}</span>
                    </span>
                    <span className="text-xs text-gray-500">{t('badges:profile.levelNumber', { level: engagement.level ?? 1 })}</span>
                  </div>
                </>
              )}
            </div>
          )}

          {showScore && !limited && engagement?.levelProgress && (
            <LevelProgressCard progress={engagement.levelProgress} own={isOwnProfile} className="mt-4 rounded-xl border border-gray-200 p-4" />
          )}

          <div className="mt-5 flex flex-wrap items-center gap-2">
            {isOwnProfile ? (
              <Button onClick={() => { onOpenChange(false); navigate(ROUTES.accountProfile) }} className="gap-2" variant="outline">
                <Pencil className="h-4 w-4" />{t('profileDialog.editProfile')}
              </Button>
            ) : needsConnection ? (
              <ConnectionActions profileId={memberId} state={connection} size="default"
                onChange={(next) => setMember((m) => (m ? { ...m, connection: next } : m))} />
            ) : canMessage ? (
              <Button variant="outline" className="gap-2" onClick={startChat} disabled={openingChat} data-chat-send-message>
                <MessageCircle className="h-4 w-4" />{t('chat:profile.sendMessage')}
              </Button>
            ) : null}
            {canAdjust && !limited && (
              <Button variant="outline" className="gap-1.5" onClick={() => setAdjustOpen(true)} data-profile-adjust-points>
                <Coins className="h-4 w-4" />{t('badges:points.open')}
              </Button>
            )}
            {pageExists && (
              <Button variant="outline" className="ml-auto gap-1.5" onClick={openPage} data-profile-open-page>
                {t('page.viewFull')}<ArrowRight className="h-4 w-4" />
              </Button>
            )}
          </div>
          {messageError && <p className="mt-3 text-sm text-red-600">{messageError}</p>}
        </div>
      </DialogContent>

      {canAdjust && (
        <AdjustPointsDialog open={adjustOpen} onOpenChange={setAdjustOpen} memberId={memberId} memberName={shownName} onSaved={() => void reload(true)} />
      )}

    </Dialog>
  )
}
